import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchSites } from '../../../site/siteSlice';
import { fetchCustomers, selectCustomers } from '../../../customer/customerSlice';
import { selectSites } from '../../../site/siteSlice';

export const useOrderData = (preSelectedCustomer?: any, preSelectedSite?: any, preSelectedSites?: any[]) => {
  const dispatch = useDispatch();
  const { customerId, siteId } = useParams();
  
  // Redux state
  const customers = useSelector(selectCustomers);
  const sites = useSelector(selectSites);
  
  // Debug Redux state
  console.log('🔍 useOrderData Redux state:', {
    sites: sites,
    sitesLength: sites?.length,
    customers: customers,
    customersLength: customers?.length
  });
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedSite, setSelectedSite] = useState<any>(null);
  
  // Track if we've already initialized to prevent infinite loops
  const hasInitialized = useRef(false);

  // Load customers on component mount (only if no pre-selected customer and customers list is empty)
  useEffect(() => {
    if (!preSelectedCustomer && customers.length === 0) {
      dispatch(fetchCustomers({}) as any);
    }
  }, [dispatch, preSelectedCustomer, customers.length]);

  // Load sites based on customer selection
  useEffect(() => {
    // Only fetch sites if we don't have a pre-selected customer (which means we're not coming from customer list)
    if (!preSelectedCustomer && !customerId) {
      if (selectedCustomer?.id) {
        console.log('Fetching sites for customer:', selectedCustomer.id);
        dispatch(fetchSites({ customerId: selectedCustomer.id }) as any);
      } else {
        // If no customer selected, fetch all sites
        console.log('No customer selected, fetching all sites');
        dispatch(fetchSites({}) as any);
      }
    }
    // If we have preSelectedCustomer or customerId, the sites are already loaded in CreateOrderPage
  }, [selectedCustomer, customerId, dispatch, preSelectedCustomer]);

  // Set selected customer and site based on URL params or pre-selected values
  useEffect(() => {
    console.log('🔄 useOrderData useEffect triggered:', {
      hasInitialized: hasInitialized.current,
      preSelectedCustomer: !!preSelectedCustomer,
      preSelectedSite: !!preSelectedSite,
      selectedCustomer: !!selectedCustomer,
      selectedSite: !!selectedSite,
      customerId,
      siteId,
      customersLength: customers.length,
      sitesLength: sites.length
    });

    // Handle pre-selected customer (always set if provided)
    if (preSelectedCustomer) {
      console.log('Setting pre-selected customer:', preSelectedCustomer);
      setSelectedCustomer(preSelectedCustomer);
      hasInitialized.current = true;
    }
    
    // Handle pre-selected site (always set if provided)
    if (preSelectedSite) {
      console.log('Setting pre-selected site:', preSelectedSite);
      setSelectedSite(preSelectedSite);
      hasInitialized.current = true;
    }
    
    // Handle URL params (only if no pre-selected values)
    if (!preSelectedCustomer && customerId && customers.length > 0) {
      const customer = customers.find(c => c.id === customerId);
      if (customer && customer.id !== selectedCustomer?.id) {
        console.log('Setting customer from URL params:', customer);
        setSelectedCustomer(customer);
        hasInitialized.current = true;
      }
    }
    
    if (!preSelectedSite && siteId && sites.length > 0) {
      const site = sites.find(s => s.id === siteId);
      if (site && site.id !== selectedSite?.id) {
        console.log('Setting site from URL params:', site);
        setSelectedSite(site);
        hasInitialized.current = true;
        // Auto-select customer for site from URL params
        if (site.customerId && customers.length > 0) {
          const customer = customers.find(c => c.id === site.customerId);
          if (customer && customer.id !== selectedCustomer?.id) {
            console.log('Auto-selecting customer for site from URL:', customer);
            setSelectedCustomer(customer);
          }
        }
      }
    }
  }, [customerId, siteId, customers, sites, preSelectedCustomer, preSelectedSite]);

  // Function to handle customer selection
  const handleCustomerChange = (customer: any) => {
    setSelectedCustomer(customer);
    setSelectedSite(null); // Reset site when customer changes
  };

  // Function to handle site selection
  const handleSiteChange = (site: any) => {
    setSelectedSite(site);
    
    // Auto-select customer if site has a customer assigned
    if (site?.customerId && customers.length > 0) {
      const customer = customers.find(c => c.id === site.customerId);
      if (customer && customer.id !== selectedCustomer?.id) {
        console.log('Auto-selecting customer for site:', customer);
        setSelectedCustomer(customer);
      }
    }
  };

  // Use preSelectedSites if available, otherwise use Redux sites
  const availableSites = preSelectedSites && preSelectedSites.length > 0 ? preSelectedSites : sites;
  
  console.log('🔍 useOrderData sites debug:', {
    preSelectedSites: preSelectedSites,
    preSelectedSitesLength: preSelectedSites?.length,
    reduxSites: sites,
    reduxSitesLength: sites?.length,
    availableSites: availableSites,
    availableSitesLength: availableSites?.length
  });
  
  // Filter sites based on selected customer
  const filteredSites = selectedCustomer 
    ? availableSites.filter(site => site.customerId === selectedCustomer.id)
    : availableSites;
    
  console.log('🔍 useOrderData filtered sites:', {
    selectedCustomer: selectedCustomer,
    filteredSites: filteredSites,
    filteredSitesLength: filteredSites?.length
  });

  return {
    selectedCustomer,
    selectedSite,
    customers,
    sites: filteredSites, // Return filtered sites
    handleCustomerChange,
    handleSiteChange
  };
};
